/**
 * Agent 数据源管理 Modal
 *
 * 功能：
 * - 展示当前页面使用的 Agent 验证记录
 * - 允许用户选择包含/排除某个 Agent 的结果
 * - 切换后自动刷新页面数据
 *
 * 面向用户的设计原则：
 * - 不暴露 Agent 内部术语
 * - 用通俗的「验证来源」概念
 * - 状态展示清晰直观
 */

import React from "react";
import { Modal, Switch, Tag, Typography, Space, Divider, Empty, Spin } from "antd";
import {
    ExperimentOutlined,
    SafetyOutlined,
    CheckCircleFilled,
    CloseCircleFilled,
    MinusCircleOutlined,
    ClockCircleOutlined,
    EyeInvisibleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

const { Text, Paragraph } = Typography;

/** Agent 来源的图标和颜色映射 */
const SOURCE_CONFIG = {
    batch_validation: {
        icon: <ExperimentOutlined style={{ fontSize: 18 }} />,
        color: "#1890ff",
    },
    cross_validation: {
        icon: <SafetyOutlined style={{ fontSize: 18 }} />,
        color: "#722ed1",
    },
};

/** 状态展示映射 */
const STATUS_CONFIG = {
    COMPLETED: { tag: <Tag color="green">已完成</Tag>, icon: <CheckCircleFilled style={{ color: "#52c41a" }} /> },
    HAS_ISSUES: { tag: <Tag color="red">有异常</Tag>, icon: <CloseCircleFilled style={{ color: "#ff4d4f" }} /> },
    PASSED: { tag: <Tag color="green">通过</Tag>, icon: <CheckCircleFilled style={{ color: "#52c41a" }} /> },
    NOT_RUN: { tag: <Tag color="default">未执行</Tag>, icon: <MinusCircleOutlined style={{ color: "#d9d9d9" }} /> },
    EXCLUDED: { tag: <Tag color="default">已排除</Tag>, icon: <EyeInvisibleOutlined style={{ color: "#d9d9d9" }} /> },
    UNKNOWN: { tag: <Tag color="default">未知</Tag>, icon: <ClockCircleOutlined style={{ color: "#faad14" }} /> },
};

/**
 * 单个 Agent 来源卡片
 */
const AgentSourceCard = ({ source, onToggle, loading }) => {
    const config = SOURCE_CONFIG[source.key] || SOURCE_CONFIG.batch_validation;
    const statusCfg = STATUS_CONFIG[source.status] || STATUS_CONFIG.UNKNOWN;

    return (
        <div
            style={{
                padding: "14px 16px",
                borderRadius: 8,
                border: `1px solid ${source.enabled ? config.color + "40" : "#f0f0f0"}`,
                background: source.enabled ? config.color + "08" : "#fafafa",
                marginBottom: 10,
                transition: "all 0.2s",
            }}
        >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Space size="middle">
                    <span style={{ color: source.enabled ? config.color : "#d9d9d9" }}>
                        {config.icon}
                    </span>
                    <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <Text strong style={{ fontSize: 14 }}>{source.name}</Text>
                            {statusCfg.tag}
                        </div>
                        <Paragraph
                            type="secondary"
                            style={{ fontSize: 12, margin: "4px 0 0", maxWidth: 360 }}
                            ellipsis={{ rows: 2 }}
                        >
                            {source.description}
                        </Paragraph>
                    </div>
                </Space>

                <Switch
                    checked={source.enabled}
                    onChange={(checked) => onToggle(source.key, checked)}
                    loading={loading}
                    checkedChildren="显示"
                    unCheckedChildren="隐藏"
                />
            </div>

            {/* 执行时间 */}
            {source.validated_at && (
                <div style={{ marginTop: 8, paddingLeft: 34, fontSize: 11, color: "#bfbfbf" }}>
                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                    执行时间: {dayjs(source.validated_at).format("YYYY-MM-DD HH:mm")}
                </div>
            )}
        </div>
    );
};

/**
 * Agent 数据源管理 Modal
 */
const AgentSourcesModal = ({ visible, onClose, agentSources, onToggleSource, loading }) => {
    const sources = agentSources || [];

    return (
        <Modal
            title={
                <Space>
                    <ExperimentOutlined />
                    <span>验证来源管理</span>
                </Space>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={520}
            destroyOnClose
        >
            <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                当前页面的验证结果来自多个独立的 AI 验证环节，您可以选择显示或隐藏各环节的结果。
            </Paragraph>

            {sources.length === 0 ? (
                <Empty description="暂无验证记录" />
            ) : (
                <Spin spinning={loading}>
                    {sources.map((source) => (
                        <AgentSourceCard
                            key={source.key}
                            source={source}
                            onToggle={onToggleSource}
                            loading={loading}
                        />
                    ))}
                </Spin>
            )}

            <Divider style={{ margin: "16px 0 8px" }} />
            <Paragraph type="secondary" style={{ fontSize: 11, marginBottom: 0 }}>
                提示：隐藏某个验证环节后，页面将不再展示该环节的验证标记和提示信息，但不影响底层数据。
            </Paragraph>
        </Modal>
    );
};

export default AgentSourcesModal;
